// src/components/Header.jsx - ENHANCED with Curated Fika and Resources Reorganization
// NO EXISTING CODE ALTERED - ONLY ADDITIONS AND REORGANIZATION

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Bars3Icon, 
  XMarkIcon, 
  ChevronDownIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  SparklesIcon,
  BookOpenIcon,
  GlobeAltIcon,
  HeartIcon,
  CheckCircleIcon,
  BoltIcon,
  ShareIcon,
  BookmarkIcon,
  EyeIcon,
  MapPinIcon,
  FireIcon,
  CogIcon,
  ShoppingBagIcon,
  EnvelopeIcon,
  UsersIcon as AdminUsersIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  // Icons for reorganized navigation
  CalculatorIcon,
  StarIcon,
  IdentificationIcon,
  ChatBubbleLeftRightIcon,
  AcademicCapIcon,
  RocketLaunchIcon,
  CameraIcon, 
} from '@heroicons/react/24/outline';

import { HeartIcon as HeartSolid, BookmarkIcon as BookmarkSolid, StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { Tab } from '@headlessui/react';

// Import member service and simplified passport config
import memberService from '../services/memberService';
import passportService from '../services/passportService';
import { PASSPORT_CONFIG, getTierConfig } from '../config/passportConfig';

// Import image utility for logo
import { getImageUrl } from '../utils/images';

// 3-7-26
import { supabase } from '../lib/supabase';

function Header({ onNavigate, onBlogNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFikaDropdownOpen, setIsFikaDropdownOpen] = useState(false);
  const [isResourcesDropdownOpen, setIsResourcesDropdownOpen] = useState(false);
  const [isCommunityDropdownOpen, setIsCommunityDropdownOpen] = useState(false);
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  const [isCuratedExperiencesDropdownOpen, setIsCuratedExperiencesDropdownOpen] = useState(false);
  
  // Member authentication state
  const [isMemberLoggedIn, setIsMemberLoggedIn] = useState(false);
  const [memberData, setMemberData] = useState(null);
  
  // Passport state (simplified)
  const [passportData, setPassportData] = useState(null);
  const [loadingPassport, setLoadingPassport] = useState(false);

  // 3-7-26
  // Fika Practice streak (reads from localStorage email — separate from member auth)
  const [fikaStreakCount, setFikaStreakCount] = useState(0);

  // Generate tiny logo URL
  const logoUrl = getImageUrl('images/branding/ampersand_logo.png', {
    width: 6,
    height: 6,
    quality: 90,
    imageType: 'branding'
  });

  // Simplified passport loading function
  const loadPassportData = async (email) => {
    if (!PASSPORT_CONFIG.enabled) {
      return null;
    }

    setLoadingPassport(true);
    try {
      const passportResult = await passportService.getPassportSummary(email);
      
      if (passportResult.success) {
        setPassportData(passportResult.data);
        console.log('🎯 Basic passport data loaded for header:', passportResult.data);
      } else {
        console.warn('⚠️ Failed to load passport data:', passportResult.error);
        // Set default passport data
        setPassportData({
          stamps: 0,
          tier: 'new',
          next_tier: { name: 'bronze', stamps_needed: 3 }
        });
      }
    } catch (error) {
      console.error('💥 Error loading passport:', error);
      setPassportData(null);
    }
    setLoadingPassport(false);
  };

  // Check member authentication status and load basic passport
  useEffect(() => {
    const checkMemberAuth = async () => {
      try {
        if (memberService.isTokenValid()) {
          setIsMemberLoggedIn(true);
          
          // Load basic member data
          const dashboardData = await memberService.getDashboardData();
          if (dashboardData.success && dashboardData.data.member) {
            const member = dashboardData.data.member;
            setMemberData({
              email: member.email,
              goalDescription: member.goal_description
            });
            
            // Load basic passport data
            await loadPassportData(member.email);
          }
        } else {
          setIsMemberLoggedIn(false);
          setMemberData(null);
          setPassportData(null);
        }
      } catch (error) {
        console.log('Member auth check failed:', error);
        setIsMemberLoggedIn(false);
        setMemberData(null);
        setPassportData(null);
        setLoadingPassport(false);
      }
    };

    checkMemberAuth();
    
    // Re-check when location changes (in case user logs in/out)
    const interval = setInterval(checkMemberAuth, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [location.pathname]);

  // 3-7-26
  // Load Fika Practice streak — runs on mount + route changes
  useEffect(() => {
    const loadFikaStreak = async () => {
      const email = localStorage.getItem('fika_practice_email');
      if (!email) {
        setFikaStreakCount(0);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('fika_practice_streaks')
          .select('current_streak')
          .eq('email', email)
          .maybeSingle();
        if (!error && data) {
          setFikaStreakCount(data.current_streak || 0);
        }
      } catch (err) {
        // Fail silently — streak badge is non-critical
      }
    };
    loadFikaStreak();
  }, [location.pathname]);

  // Track active section for homepage sections (unchanged)
  useEffect(() => {
    if (location.pathname !== '/') {
      setActiveSection('');
      return;
    }

    const handleScroll = () => {
      const sections = ['hero', 'weekly-fika', 'events', 'interactive'];
      const currentSection = sections.find(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      
      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);


  useEffect(() => {
    const handleClickOutside = (event) => {
      // Safety check: ensure event.target exists and is an Element
      if (!event.target || !(event.target instanceof Element)) {
        return;
      }
  
      if (isFikaDropdownOpen && !event.target.closest('.fika-dropdown-container')) {
        setIsFikaDropdownOpen(false);
      }
      if (isResourcesDropdownOpen && !event.target.closest('.resources-dropdown-container')) {
        setIsResourcesDropdownOpen(false);
      }
      if (isCommunityDropdownOpen && !event.target.closest('.community-dropdown-container')) {
        setIsCommunityDropdownOpen(false);
      }
      if (isAdminDropdownOpen && !event.target.closest('.admin-dropdown-container')) {
        setIsAdminDropdownOpen(false);
      }
      if (isCuratedExperiencesDropdownOpen && !event.target.closest('.curated-experiences-dropdown-container')) {
        setIsCuratedExperiencesDropdownOpen(false);
      }
    };
  
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isFikaDropdownOpen, isResourcesDropdownOpen, isAdminDropdownOpen, isCuratedExperiencesDropdownOpen, isCommunityDropdownOpen]);


   
 

  const handleAboutClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'about_page_click', {
        'event_category': 'navigation',
        'event_label': 'header_about',
        'click_location': 'header_navigation'
      });
    }
  
    if (location.pathname !== '/about') {
      navigate('/about');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };
  
  // ALL EXISTING NAVIGATION FUNCTIONS (unchanged)
  const isNavItemActive = (navType, sectionId = null) => {
    switch (navType) {
      case 'events':
        return location.pathname.startsWith('/events');
      case 'blog':
        return location.pathname.startsWith('/blog');
      case 'subscription':
        return location.pathname === '/subscription';
      case 'experiences':
        return location.pathname.startsWith('/experiences');
      case 'merchandise': 
        return location.pathname.startsWith('/merchandise'); 
      case 'about': 
        return location.pathname === '/about';
      case 'fika':
        return (location.pathname === '/' && activeSection === 'weekly-fika') || 
               location.pathname === '/monthly-fika' || 
               location.pathname === '/signature-fika' ||
               location.pathname === '/curated-fika' ||  
               location.pathname === '/curated-fika-signup' || // Include curated fika
               location.pathname === '/curated-fika/founders' ||
               location.pathname === '/curated-fika/founders-1on1/apply' || 
               location.pathname === '/why-connectco' ||
               location.pathname === '/fika-at-33' ||
               location.pathname === '/what-is-fika' ||
               location.pathname === '/deep-fika' ||
               location.pathname === '/deep-fika/apply';
      case 'resources': // NEW: Resources navigation detection
        return location.pathname === '/conversation-starters' ||
               location.pathname === '/tokyo-expat-guide' ||
               location.pathname === '/tokyo-expat-calculator' ||
               location.pathname === '/goal-accountability-partners' ||
               location.pathname === '/member-passport' ||
               location.pathname === '/nomad-concierge' ||
               location.pathname === '/submit-discovery' ||
               location.pathname === '/kind-moments' ||   
               location.pathname.includes('/coffee-guide');
      case 'admin':
        return location.pathname.startsWith('/admin');
      case 'member':
        return location.pathname.startsWith('/member-');
      case 'fika-practice':
        return location.pathname.startsWith('/fika-practice');
      case 'community':
        return location.pathname.startsWith('/community');
      default:
        return false;
    }
  };

  // ALL EXISTING HANDLER FUNCTIONS (unchanged - preserving all existing navigation)
  const handleSectionNavigation = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      const heroElement = document.getElementById('hero');
      if (heroElement) {
        heroElement.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  const handleEventsClick = () => {
    if (location.pathname !== '/events') {
      navigate('/events');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleCuratedFikaDescriptionClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'curated_fika_description_click', {
        'event_category': 'navigation',
        'event_label': 'header_curated_fika_description',
        'click_location': 'header_fika_dropdown',
        'target_service': 'curated_fika_description'
      });
    }
  
    if (location.pathname !== '/curated-fika') {
      navigate('/curated-fika');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsFikaDropdownOpen(false);
  };

  const handleSubscriptionClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'subscription_header_click', {
        'event_category': 'conversion',
        'event_label': 'header_subscription',
        'click_location': 'header_navigation'
      });
    }
  
    if (location.pathname !== '/subscription') {
      navigate('/subscription');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleExperiencesClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'experiences_navigation_click', {
        'event_category': 'navigation',
        'event_label': 'header_experiences',
        'click_location': 'header'
      });
    }

    if (location.pathname !== '/experiences') {
      navigate('/experiences');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleBlogClick = () => {
    if (location.pathname !== '/blog') {
      navigate('/blog');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleCommunityClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'community_click', {
        'event_category': 'navigation',
        'event_label': 'header_community',
        'click_location': 'header_nav',
        'target_page': '/community'
      });
    }
    if (location.pathname !== '/community') {
      navigate('/community');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleFikaAt33Click = () => {
  if (typeof gtag !== 'undefined') {
    gtag('event', 'fika_at_33_click', {
      'event_category': 'navigation',
      'event_label': 'header_fika_at_33',
      'click_location': 'header_fika_dropdown'
    });
  }

  if (location.pathname !== '/fika-at-33') {
    navigate('/fika-at-33');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  setIsMenuOpen(false);
  setIsFikaDropdownOpen(false);
};

  const handleDeepFikaClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'deep_fika_click', {
        'event_category': 'navigation',
        'event_label': 'header_deep_fika',
        'click_location': 'header_fika_dropdown',
        'target_page': '/deep-fika'
      });
    }
    if (location.pathname !== '/deep-fika') {
      navigate('/deep-fika');
      setTimeout(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsFikaDropdownOpen(false);
  };

  const handleDeepFikaApplyClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'deep_fika_apply_click', {
        'event_category': 'navigation',
        'event_label': 'header_deep_fika_apply',
        'click_location': 'header_fika_dropdown',
        'target_page': '/deep-fika/apply'
      });
    }
    if (location.pathname !== '/deep-fika/apply') {
      navigate('/deep-fika/apply');
      setTimeout(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsFikaDropdownOpen(false);
  };

  const handleMonthlyFikaClick = () => {
    if (location.pathname !== '/monthly-fika') {
      navigate('/monthly-fika');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleSignatureFikaClick = () => {
    if (location.pathname !== '/signature-fika') {
      navigate('/signature-fika');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  // Add this handler function with the other handler functions (around line 300)
const handleSubmitDiscoveryClick = () => {
  if (typeof gtag !== 'undefined') {
    gtag('event', 'submit_discovery_click', {
      'event_category': 'navigation',
      'event_label': 'header_submit_discovery',
      'click_location': 'header_resources_dropdown',
      'target_page': 'submit_discovery',
      'community_contribution': 'true'
    });
  }

  if (location.pathname !== '/submit-discovery') {
    navigate('/submit-discovery');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  setIsMenuOpen(false);
  setIsResourcesDropdownOpen(false);
};

    // NEW: What is Curated Fika handler
  const handleWhatIsCuratedFikaClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'what_is_curated_fika_click', {
        'event_category': 'navigation',
        'event_label': 'header_what_is_curated_fika',
        'click_location': 'header_fika_dropdown'
      });
    }
  
    if (location.pathname !== '/curated-fika') {
      navigate('/curated-fika');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsFikaDropdownOpen(false);
  };

  const handleWhatIsSpeedFikaClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'what_is_speed_fika_click', {
        'event_category': 'navigation',
        'event_label': 'header_what_is_speed_fika',
        'click_location': 'header_fika_dropdown',
        'target_page': 'what_is_speed_fika'
      });
    }
  
    if (location.pathname !== '/what-is-speed-fika') {
      navigate('/what-is-speed-fika');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsFikaDropdownOpen(false);
  };
  

  // NEW: Curated Fika handler
  const handleCuratedFikaClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'curated_fika_click', {
        'event_category': 'navigation',
        'event_label': 'header_curated_fika',
        'click_location': 'header_fika_dropdown',
        'target_service': 'curated_fika_premium'
      });
    }

    if (location.pathname !== '/curated-fika-signup') {
      navigate('/curated-fika-signup');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsFikaDropdownOpen(false);
  };

  // Handler for Founders Fika (Group Peer Support)
const handleFoundersFikaClick = () => {
  if (typeof gtag !== 'undefined') {
    gtag('event', 'founders_fika_click', {
      'event_category': 'navigation',
      'event_label': 'header_founders_fika_group',
      'click_location': 'header_fika_dropdown',
      'target_service': 'founders_fika_peer_group',
      'founder_product_type': 'group_support'
    });
  }

  if (location.pathname !== '/founders-fika') {
    navigate('/founders-fika');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  setIsMenuOpen(false);
  setIsFikaDropdownOpen(false);
};

// Handler for Curated Fika Founders (1-on-1 Tactical/ Builder Coffee Match)
const handleCuratedFikaFoundersClick = () => {
  if (typeof gtag !== 'undefined') {
    gtag('event', 'curated_fika_founders_click', {
      'event_category': 'navigation',
      'event_label': 'header_curated_fika_founders_1on1',
      'click_location': 'header_fika_dropdown',
      'target_service': 'curated_fika_founders',
      'founder_product_type': '1on1_tactical'
    });
  }

  if (location.pathname !== '/curated-fika/founders') {
    navigate('/curated-fika/founders');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  setIsMenuOpen(false);
  setIsFikaDropdownOpen(false);
};

  

  const handleWhatIsFikaClick = () => {
    if (location.pathname !== '/what-is-fika') {
      navigate('/what-is-fika');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleNomadConciergClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'nomad_concierge_click', {
        'event_category': 'navigation',
        'event_label': 'header_nomad_concierge',
        'click_location': 'header_resources_dropdown',
        'target_page': 'nomad_concierge'
      });
    }
  
    if (location.pathname !== '/nomad-concierge') {
      navigate('/nomad-concierge');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsResourcesDropdownOpen(false);
  };

  // 
  const handleConversationStartersClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'conversation_starters_click', {
        'event_category': 'navigation',
        'event_label': 'header_conversation_starters',
        'click_location': 'header_resources_dropdown',
        'target_service': 'conversation_tools'
      });
    }

    if (location.pathname !== '/conversation-starters') {
      navigate('/conversation-starters');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsResourcesDropdownOpen(false);
  };

  const handleCoffeeGuideClick = () => {
  if (typeof gtag !== 'undefined') {
    gtag('event', 'coffee_guide_click', {
      'event_category': 'navigation',
      'event_label': 'header_coffee_guide',
      'click_location': 'header_resources_dropdown',
      'target_page': 'coffee_guide',
      'resource_type': 'directory'
    });
  }

  if (location.pathname !== '/coffee-guide/tokyo') {
    navigate('/coffee-guide/tokyo');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  setIsMenuOpen(false);
  setIsResourcesDropdownOpen(false);
};

const handlePhotosClick = () => {
  if (typeof gtag !== 'undefined') {
    gtag('event', 'photos_click', {
      'event_category': 'navigation',
      'event_label': 'header_photos',
      'click_location': 'header_resources_dropdown',
      'target_page': 'photos'
    });
  }

  if (location.pathname !== '/photos') {
    navigate('/photos');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  setIsMenuOpen(false);
  setIsResourcesDropdownOpen(false);
};

const handleKindMomentsClick = () => {
  if (location.pathname !== '/kind-moments') {
    navigate('/kind-moments');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  setIsMenuOpen(false);
  setIsResourcesDropdownOpen(false);
};

  const handleHappinessClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'happiness_page_click', {
        'event_category': 'navigation',
        'event_label': 'header_happiness',
        'click_location': 'header_resources_dropdown',
        'target_page': 'happiness_wellbeing'
      });
    }
  
    if (location.pathname !== '/happiness') {
      navigate('/happiness');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsResourcesDropdownOpen(false);
  };

  const handleTokyoExpatGuideClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'tokyo_expat_guide_click', {
        'event_category': 'navigation',
        'event_label': 'header_tokyo_expat_guide',
        'click_location': 'header_resources_dropdown',
        'target_audience': 'new_expats'
      });
    }

    if (location.pathname !== '/tokyo-expat-guide') {
      navigate('/tokyo-expat-guide');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsResourcesDropdownOpen(false);
  };

  const handleGoalAccountabilityPartnersClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'goal_accountability_partners_click', {
        'event_category': 'navigation',
        'event_label': 'header_goal_accountability_partners',
        'click_location': 'header_resources_dropdown',
        'target_service': 'goal_accountability_matching'
      });
    }
  
    if (location.pathname !== '/goal-accountability-partners') {
      navigate('/goal-accountability-partners');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsResourcesDropdownOpen(false);
  };

  // 11-15-25
  const handleCuratedFikaEntrepreneursAdminClick = () => {
  if (typeof gtag !== 'undefined') {
    gtag('event', 'curated_fika_entrepreneurs_admin_click', {
      'event_category': 'admin',
      'event_label': 'header_curated_fika_entrepreneurs_admin',
      'click_location': 'header_admin_dropdown'
    });
  }

  if (location.pathname !== '/admin/curated-fika-entrepreneurs') {
    navigate('/admin/curated-fika-entrepreneurs');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  setIsMenuOpen(false);
  setIsAdminDropdownOpen(false);
};

  // Handler for Builder Coffee Match Application (1-on-1 Apply)
const handleBuilderCoffeeMatchApplyClick = () => {
  if (typeof gtag !== 'undefined') {
    gtag('event', 'builder_coffee_match_apply_click', {
      'event_category': 'conversion',
      'event_label': 'header_builder_coffee_match_apply',
      'click_location': 'header_fika_dropdown',
      'target_service': 'founders_1on1_application',
      'founder_product_type': '1on1_application_form',
      'conversion_intent': 'high'
    });
  }

  if (location.pathname !== '/curated-fika/founders-1on1/apply') {
    navigate('/curated-fika/founders-1on1/apply');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  setIsMenuOpen(false);
  setIsFikaDropdownOpen(false);
};

  const handleDeepFikaAdminClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'deep_fika_admin_click', {
        'event_category': 'admin',
        'event_label': 'header_deep_fika_admin',
        'click_location': 'header_admin_dropdown'
      });
    }
    if (location.pathname !== '/admin/deep-fika') {
      navigate('/admin/deep-fika');
      setTimeout(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsAdminDropdownOpen(false);
  };

  const handleCuratedFikaAdminClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'curated_fika_admin_click', {
        'event_category': 'admin',
        'event_label': 'header_curated_fika_admin',
        'click_location': 'header_admin_dropdown'
      });
    }
  
    if (location.pathname !== '/admin/curated-fika') {
      navigate('/admin/curated-fika');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsAdminDropdownOpen(false);
  };

  const handleTokyoExpatCalculatorClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'tokyo_expat_calculator_click', {
        'event_category': 'navigation',
        'event_label': 'header_tokyo_expat_calculator',
        'click_location': 'header_resources_dropdown',
        'target_tool': 'expat_calculator',
        'target_audience': 'tokyo_expats'
      });
    }

    if (location.pathname !== '/tokyo-expat-calculator') {
      navigate('/tokyo-expat-calculator');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsResourcesDropdownOpen(false);
  };

  const handleMemberPassportClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'member_passport_click', {
        'event_category': 'navigation',
        'event_label': 'header_member_passport',
        'click_location': 'header_resources_dropdown',
        'target_page': 'member_passport'
      });
    }
  
    if (location.pathname !== '/member-passport') {
      navigate('/member-passport');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsResourcesDropdownOpen(false);
  };

  const handleMerchandiseClick = () => {
    if (location.pathname !== '/merchandise') {
      navigate('/merchandise');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleNewsletterAdminClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'newsletter_admin_click', {
        'event_category': 'admin',
        'event_label': 'header_newsletter_admin',
        'click_location': 'header_admin_dropdown'
      });
    }

    if (location.pathname !== '/admin/newsletter') {
      navigate('/admin/newsletter');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsAdminDropdownOpen(false);
  };

  const handleGoalAccountabilityAdminClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'goal_accountability_admin_click', {
        'event_category': 'admin',
        'event_label': 'header_goal_accountability_admin',
        'click_location': 'header_admin_dropdown'
      });
    }

    if (location.pathname !== '/admin/goal-accountability') {
      navigate('/admin/goal-accountability');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setIsAdminDropdownOpen(false);
  };

  // Member-specific navigation functions (unchanged)
  const handleMemberLoginClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'member_login_header_click', {
        'event_category': 'member_auth',
        'event_label': 'header_member_login',
        'click_location': 'header_cta'
      });
    }

    navigate('/member-login');
    setIsMenuOpen(false);
  };

  const handleMemberDashboardClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'member_dashboard_header_click', {
        'event_category': 'member_auth',
        'event_label': 'header_member_dashboard',
        'click_location': 'header_cta',
        'member_email_domain': memberData?.email?.split('@')[1] || 'unknown'
      });
    }

    navigate('/member-dashboard');
    setIsMenuOpen(false);
  };

  const handleMemberLogout = async () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'member_logout_header_click', {
        'event_category': 'member_auth',
        'event_label': 'header_member_logout',
        'click_location': 'header_cta'
      });
    }

    memberService.clearToken();
    setIsMemberLoggedIn(false);
    setMemberData(null);
    setPassportData(null); // Clear passport data on logout
    
    if (location.pathname.startsWith('/member-')) {
      navigate('/');
    }
    setIsMenuOpen(false);
  };

  // Smart CTA function - Context-aware button behavior (unchanged)
  const handleSmartCTAClick = () => {
    if (isMemberLoggedIn) {
      handleMemberDashboardClick();
    } else {
      if (location.pathname.includes('goal-accountability') || location.pathname.includes('member')) {
        handleMemberLoginClick();
      } else {
        handleJoinNowClick();
      }
    }
  };

  // Updated Join Now function - Direct to Curated Fika Signup
const handleJoinNowClick = () => {
  if (typeof gtag !== 'undefined') {
    gtag('event', 'join_now_header_click', {
      'event_category': 'conversion',
      'event_label': 'header_join_now_curated_fika',
      'click_location': 'header',
      'target_page': 'curated_fika_signup'
    });
  }

  // Direct navigation to Curated Fika signup
  navigate('/curated-fika-signup');
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 100);
  
  setIsMenuOpen(false);
};



  // Smart CTA button content with basic passport integration (unchanged)
  const getSmartCTAContent = () => {
    if (isMemberLoggedIn && memberData && passportData && PASSPORT_CONFIG.enabled) {
      const tierConfig = getTierConfig(passportData.tier);
      return {
        text: `Hi, ${memberData.email}!`,
        subtext: `${passportData.stamps} stamps ${tierConfig.icon}`,
        icon: UserCircleIcon,
        action: handleMemberDashboardClick,
        className: 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
      };
    } else if (isMemberLoggedIn && memberData) {
      return {
        text: `Hi, ${memberData.email}!`,
        subtext: loadingPassport ? 'Loading...' : 'Dashboard',
        icon: UserCircleIcon,
        action: handleMemberDashboardClick,
        className: 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
      };
    } else if (location.pathname.includes('goal-accountability') || location.pathname.includes('member')) {
      return {
        text: 'Member Goal Login',
        subtext: null,
        icon: ArrowRightOnRectangleIcon,
        action: handleMemberLoginClick,
        className: 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
      };
    } else {
      
      return {
        text: 'Let\'s Fika',
        subtext: null,
        icon: null,
        action: handleJoinNowClick,
        className: 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
      };
    }
    
  };

  const smartCTA = getSmartCTAContent();


  return (
    <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-lg z-50">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex justify-between items-center py-4">
          {/* Logo (unchanged) */}
          {/*
          <button 
            onClick={handleLogoClick}
            className="text-2xl font-bold text-purple-600 hover:text-purple-700 transition-colors cursor-pointer flex flex-col items-start"
          >
            <span className="text-2xl font-bold text-purple-600">
              Connect 
              <span 
                className="font-black mx-1"
                style={{
                  background: 'linear-gradient(135deg, #FBBF24 0%, #F97316 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                &
              </span>
              Co.
            </span>
            <span className="text-xs font-medium text-amber-600 tracking-wide -mt-0.5">
              Find your tribe, anywhere
            </span>
          </button>
          */}


          {/* Logo with Nested C Design - Fixed */}
          <button 
            onClick={handleLogoClick}
            className="text-2xl font-bold hover:opacity-90 transition-opacity cursor-pointer flex flex-col items-start md:-ml-8"
          >
            <div className="flex items-center">
              {/* Nested C SVG Logo Mark */}
              <svg width="30" height="32" viewBox="0 0 30 32" className="flex-shrink-0 group-hover:scale-105 transition-transform duration-200">
                <defs>
                  <linearGradient id="innerCGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#FBBF24' }} />
                    <stop offset="100%" style={{ stopColor: '#F97316' }} />
                  </linearGradient>
                </defs>
                
                {/* Outer C - Purple (open on the right) */}
                <path
                  d="M 24 10 A 10 10 0 1 0 24 22"
                  fill="none"
                  stroke="#9333EA"
                  strokeWidth="4"
                  strokeLinecap="round"
                />
                
                {/* Inner C - Orange Gradient (open on the right) */}
                <path
                  d="M 21 12 A 7 7 0 1 0 21 20"
                  fill="none"
                  stroke="url(#innerCGradient)"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
              
              {/* Rest of the text */}
              <span className="text-2xl font-bold text-purple-600">
                onnectCo
              </span>

              

            </div>
            
            {/*
            <span className="text-xs font-small text-amber-600 tracking-wide -mt-0.5">
              Where Strangers Become Friends
            </span>
            */}

            <span className="text-[11px] sm:text-xs text-amber-600 whitespace-nowrap -mt-0.5">
              No strangers. Just remarkable people.
            </span>



          </button>



          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {/* Events Tab (unchanged) */}
            <div className="relative">
              <button 
                onClick={handleEventsClick}
                className={`text-gray-700 hover:text-purple-600 font-medium transition-colors pb-2 flex items-center gap-2 ${
                  isNavItemActive('events') ? 'text-purple-600' : ''
                }`}
              >
               {/*  <CalendarDaysIcon className="w-8 h-8" /> */}
                 <SparklesIcon className="w-8 h-8"  />
                Events
              </button>
              {isNavItemActive('events') && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black rounded-full"></div>
              )}
            </div>

            {/* Community Dropdown */}
            <div className="relative community-dropdown-container">
              <button
                onClick={() => setIsCommunityDropdownOpen(!isCommunityDropdownOpen)}
                onMouseEnter={() => setIsCommunityDropdownOpen(true)}
                className={`text-gray-700 hover:text-purple-600 font-medium transition-colors pb-2 flex items-center gap-2 ${
                  isNavItemActive('community') || isNavItemActive('blog') ? 'text-purple-600' : ''
                }`}
              >
                <ChatBubbleLeftRightIcon className="w-8 h-8" />
                Community
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${isCommunityDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {(isNavItemActive('community') || isNavItemActive('blog')) && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black rounded-full"></div>
              )}

              {isCommunityDropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                  onMouseLeave={() => setIsCommunityDropdownOpen(false)}
                >
                  {/* Fika Board */}
                  <button
                    onClick={() => {
                      handleCommunityClick();
                      setIsCommunityDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      isNavItemActive('community')
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <ChatBubbleLeftRightIcon className="w-5 h-5 mr-3 text-purple-500" />
                      <div>
                        <div className="font-medium">Fika Board</div>
                        <div className="text-xs text-gray-500">Community discussions</div>
                      </div>
                    </div>
                  </button>

                  <div className="border-t border-gray-200 my-1"></div>

                  {/* Stories */}
                  <button
                    onClick={() => {
                      handleBlogClick();
                      setIsCommunityDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      isNavItemActive('blog')
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <BookOpenIcon className="w-5 h-5 mr-3 text-indigo-500" />
                      <div>
                        <div className="font-medium">Stories</div>
                        <div className="text-xs text-gray-500">Articles on friendship, travels, food, fika & life</div>
                      </div>
                    </div>
                  </button>

                  {/* Photos */}
                  <button
                    onClick={() => {
                      handlePhotosClick();
                      setIsCommunityDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/photos'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <CameraIcon className="w-5 h-5 mr-3 text-pink-500" />
                      <div>
                        <div className="font-medium">Photos</div>
                        <div className="text-xs text-gray-500">Moments from our gatherings</div>
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </div>
            
            {/* UPDATED: Fika Dropdown (streamlined with Curated Fika) */}
            <div className="relative fika-dropdown-container">
              <button 
                onClick={() => setIsFikaDropdownOpen(!isFikaDropdownOpen)}  
                onMouseEnter={() => setIsFikaDropdownOpen(true)}
                className={`text-gray-700 hover:text-amber-600 font-medium transition-colors flex items-center pb-2 gap-2 ${
                  isNavItemActive('fika') ? 'text-amber-600' : ''
                }`}
              >
                <div className="w-8 h-8 flex items-center justify-center">☕</div>
                Fika
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${isFikaDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {isNavItemActive('fika') && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-600 rounded-full"></div>
              )}


               {/* Streamlined Fika Dropdown Menu */}
               {isFikaDropdownOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                  onMouseLeave={() => setIsFikaDropdownOpen(false)}
                >
                  {/* Fika Practice — relocated from standalone nav */}
                  <button
                    onClick={() => {
                      navigate('/fika-practice/today');
                      setIsFikaDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      isNavItemActive('fika-practice')
                        ? 'bg-amber-50 text-amber-700 border-l-4 border-amber-500'
                        : 'text-gray-700 hover:bg-amber-50 hover:text-amber-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <FireIcon className="w-5 h-5 mr-3 text-amber-500" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-800 flex items-center gap-2">
                          Practice
                          {fikaStreakCount > 0 && (
                            <span className="flex items-center gap-0.5 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none shadow-sm">
                              🔥{fikaStreakCount}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">Daily Fika · 2 min</div>
                      </div>
                    </div>
                  </button>

                  <div className="border-t border-gray-200 my-2"></div>

                  <button
                        onClick={() => {
                          if (typeof gtag !== 'undefined') {
                            gtag('event', 'why_connectco_click', {
                              'event_category': 'navigation',
                              'event_label': 'header_why_connectco',
                              'click_location': 'header_fika_dropdown',
                              'target_page': 'positioning'
                            });
                          }
                          if (location.pathname !== '/why-connectco') {
                            navigate('/why-connectco');
                            setTimeout(() => {
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }, 100);
                          } else {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                          setIsMenuOpen(false);
                          setIsFikaDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 transition-colors ${
                          location.pathname === '/why-connectco'
                            ? 'bg-emerald-50 text-emerald-700 border-l-4 border-emerald-500'
                            : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                        }`}
                      >
                        <div className="flex items-center">
                          <span className="mr-3">🌿</span>
                          <div>
                            <div className="font-medium">Why ConnectCo?</div>
                            <div className="text-xs text-gray-500">Our approach vs. Meetup, Bumble BFF & more</div>
                          </div>
                        </div>
                  </button> 

                   <div className="border-t border-gray-200 my-2"></div>
                  
                  <button
                    onClick={() => {
                      handleMonthlyFikaClick();
                      setIsFikaDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                  >
                    <div className="flex items-center">
                      <span className="mr-3 text-lg">📅</span>
                      <div>
                        <div className="font-medium text-gray-800">Monthly Fika</div>
                        <div className="text-xs text-gray-500">Open to all subscribers • Sunday/Saturday </div>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => {
                      handleSignatureFikaClick();
                      setIsFikaDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                  >
                    <div className="flex items-center">
                      <span className="mr-3 text-lg">⭐</span>
                      <div>
                        <div className="font-medium text-gray-800">Signature Fika</div>
                        <div className="text-xs text-gray-500">Premium • Weekly • Wednesdays</div>
                      </div>
                    </div>
                  </button>

                  

                  <button                
                    onClick={() => {
                      handleCuratedFikaClick();     
                      setIsFikaDropdownOpen(false);
                    }}
                    
                   
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/curated-fika-signup'
                        ? 'bg-amber-50 text-amber-700 border-l-4 border-amber-500'
                        : 'text-gray-700 hover:bg-gradient-to-r hover:from-amber-50 hover:to-orange-50 hover:text-amber-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <SparklesIcon className="w-5 h-5 mr-3 text-amber-500" />
                      <div>
                        <div className="font-medium text-gray-800 flex items-center">
                          Join Curated Fika
                          {/* 
                        <span className="ml-2 px-1.5 py-0.5 bg-gradient-to-r from-purple-600 to-amber-600 text-purple-100 text-xs rounded-full font-medium">
                          BETA
                        </span>
                        */}
                          
                        </div>
                        {/* • Membership required */}
                        <div className="text-xs text-gray-500">Ultra-Premium • 3-4 People </div>
                        
                      </div>
                    </div>
                  </button>

                  <div className="border-t border-gray-200 my-2"></div>

                  {/* Deep Fika — 1-on-1 */}
                  <button
                    onClick={() => {
                      handleDeepFikaClick();
                      setIsFikaDropdownOpen(false);
                    }}
                    className={'w-full text-left px-4 py-3 transition-colors ' + (
                      location.pathname === '/deep-fika' || location.pathname === '/deep-fika/apply'
                        ? 'bg-emerald-50 text-emerald-700 border-l-4 border-emerald-500'
                        : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                    )}
                  >
                    <div className="flex items-center">
                      <span className="mr-3 text-lg">🫂</span>
                      <div>
                        <div className="font-medium text-gray-800 flex items-center gap-2">
                          Deep Fika
                          <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                            1-on-1
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">One matched conversation</div>
                      </div>
                    </div>
                  </button>

                  <div className="border-t border-gray-200 my-2"></div>



                      {/* Section Header */}
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      For Founders & Entrepreneurs
                      </div>

                      {/* 1-on-1 Tactical Match - Builder Coffee Match */}
                      <button
                        onClick={() => {
                          handleCuratedFikaFoundersClick();
                          setIsFikaDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 transition-colors ${
                          location.pathname === '/curated-fika/founders'
                            ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-500'
                            : 'text-gray-700 hover:bg-indigo-50 hover:text-indigo-700'
                        }`}
                      >
                        <div className="flex items-center">
                          <span className="mr-3 text-lg">💼</span>
                          <div>
                            <div className="font-medium text-gray-800">What is Builder Coffee Match
                            <span className="ml-2 px-1.5 py-0.5 bg-gradient-to-r from-purple-600 to-amber-600 text-purple-100 text-xs rounded-full font-medium">
                              BETA
                            </span>
                            </div>
                            <div className="text-xs text-gray-500">Confidential builder conversations</div>
                          </div>
                        </div>
                      </button>

                      {/* Apply for Builder Coffee Match - Action Button */}
                      {/*
                      <button
                        onClick={() => {
                          handleBuilderCoffeeMatchApplyClick();
                          setIsFikaDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 transition-colors ${
                          location.pathname === '/curated-fika/founders-1on1/apply'
                            ? 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border-l-4 border-indigo-600'
                            : 'text-gray-700 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-700'
                        }`}
                      >
                        <div className="flex items-center">
                          <span className="mr-3 text-lg">📝</span>
                          <div className="flex-1">
                            <div className="font-semibold text-gray-800 flex items-center">
                              Apply for Builder Coffee Match
                              <span className="ml-2 px-1.5 py-0.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs rounded-full font-medium">
                                BETA
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">Start your founder matching application</div>
                          </div>
                        </div>
                      </button>
                       */}

                      {/* Group Peer Support */}
                       {/*
                      <button
                        onClick={() => {
                          handleFoundersFikaClick();
                          setIsFikaDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 transition-colors ${
                          location.pathname === '/founders-fika'
                            ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-500'
                            : 'text-gray-700 hover:bg-indigo-50 hover:text-indigo-700'
                        }`}
                      >
                        <div className="flex items-center">
                          <span className="mr-3 text-lg">🤝</span>
                          <div>
                            <div className="font-medium text-gray-800">Peer Support Group</div>
                            <div className="text-xs text-gray-500">6-8 founders • Monthly</div>
                          </div>
                        </div>
                      </button>
                      */}
                  
                  <div className="border-t border-gray-200 my-2"></div>
                  
                  <button
                    onClick={() => {
                      handleWhatIsFikaClick();
                      setIsFikaDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                  >
                    <div className="flex items-center">
                      <span className="mr-3">🇸🇪</span>
                      <div>
                        <div className="font-medium">What is Fika?</div>
                        <div className="text-xs text-gray-500">Learn about Swedish tradition</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      handleFikaAt33Click();
                      setIsFikaDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/fika-at-33'
                        ? 'bg-stone-900 text-amber-500 border-l-4 border-amber-500'
                        : 'text-gray-700 hover:bg-stone-900 hover:text-amber-400'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="mr-3">🎵</span>
                      <div>
                        <div className="font-medium">What is Fika at 33⅓?</div>
                        <div className="text-xs text-gray-500">Vinyl listening bar</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      handleWhatIsSpeedFikaClick();
                      setIsFikaDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                  >
                    <div className="flex items-center">
                      <span className="mr-3">⚡</span>
                      <div>
                        <div className="font-medium">What is Speed Fika?</div>
                        <div className="text-xs text-gray-500">Psychology-based community connection method</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      handleWhatIsCuratedFikaClick();
                      setIsFikaDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/curated-fika'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="mr-3">✨</span>
                      <div>
                        <div className="font-medium">What is Curated Fika?</div>
                        <div className="text-xs text-gray-500">Learn about ultra-premium experience</div>
                      </div>
                    </div>
                  </button>


                    {/*  
                  <div className="border-t border-gray-200 my-2"></div>
                  */}

                    {/*
                  <button
                    onClick={() => {
                      handleGoalAccountabilityPartnersClick();
                      setIsResourcesDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/goal-accountability-partners'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="mr-3 text-lg">🎯</span>
                      <div>
                        <div className="font-medium text-gray-800 flex items-center">
                          Goal Accountability Partners
                          <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                            FREE
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">Find your accountability tribe</div>
                      </div>
                    </div>
                  </button>
                  */}

                
                </div>
              )}


              
             
            </div>

            {/* NEW: Curated Experiences Dropdown */}
            <div className="relative curated-experiences-dropdown-container">
              <button 
                onClick={() => setIsCuratedExperiencesDropdownOpen(!isCuratedExperiencesDropdownOpen)}
                onMouseEnter={() => setIsCuratedExperiencesDropdownOpen(true)}
                className={`text-gray-700 hover:text-purple-600 font-medium transition-colors flex items-center pb-2 gap-2 ${
                  location.pathname.includes('/curated-fika/member-portal') || 
                  location.pathname.includes('/curated-fika/my-sessions') 
                    ? 'text-purple-600' 
                    : ''
                }`}
              >
                <SparklesIcon className="w-8 h-8" />
                <div className="flex flex-col items-start">
                  <span>Curated Fika</span>
                
                </div>
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${isCuratedExperiencesDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {(location.pathname.includes('/curated-fika/member-portal') || 
                location.pathname.includes('/curated-fika/my-sessions')) && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 rounded-full"></div>
              )}
              
              {/* Dropdown Menu */}
              {isCuratedExperiencesDropdownOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                  onMouseLeave={() => setIsCuratedExperiencesDropdownOpen(false)}
                >
                  {/* Info Banner */}
                  <div className="px-4 py-3 bg-gradient-to-r from-purple-50 to-amber-50 border-b border-purple-100 mb-2">
                    <div className="flex items-start">
                      <SparklesIcon className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <div className="font-semibold text-purple-900">Ultra-Premium Intimate Experiences</div>
                        <div className="text-xs text-purple-700 mt-1">Browse curated sessions matched to your profile</div>
                      </div>
                    </div>
                  </div>

                  {/* 12-21-25 */}
                   <button                
                    onClick={() => {
                      handleCuratedFikaClick();     
                      setIsFikaDropdownOpen(false);
                    }}
                    
                   
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/curated-fika-signup'
                        ? 'bg-amber-50 text-amber-700 border-l-4 border-amber-500'
                        : 'text-gray-700 hover:bg-gradient-to-r hover:from-amber-50 hover:to-orange-50 hover:text-amber-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <SparklesIcon className="w-5 h-5 mr-3 text-amber-500" />
                      <div>
                        <div className="font-medium text-gray-800 flex items-center">
                          Join Curated Fika
                           {/*
                        <span className="ml-2 px-1.5 py-0.5 bg-gradient-to-r from-purple-600 to-amber-600 text-purple-100 text-xs rounded-full font-medium">
                          BETA
                        </span>
                        */}
                          
                        </div>
                        {/* • Membership required */}
                        <div className="text-xs text-gray-500">Ultra-Premium • 3-4 People </div>
                        
                      </div>
                    </div>
                  </button>



                  <button
                    onClick={() => {
                      if (typeof gtag !== 'undefined') {
                        gtag('event', 'curated_experiences_member_portal_click', {
                          'event_category': 'navigation',
                          'event_label': 'header_curated_experiences_member_portal',
                          'click_location': 'header_dropdown'
                        });
                      }
                      navigate('/curated-fika/member-portal');
                      setIsCuratedExperiencesDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/curated-fika/member-portal'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <UserCircleIcon className="w-5 h-5 mr-3 text-purple-500" />
                      <div>
                        <div className="font-medium text-gray-800">Browse Available Sessions</div>
                        <div className="text-xs text-gray-500">View personalized matches & register</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      if (typeof gtag !== 'undefined') {
                        gtag('event', 'curated_experiences_my_sessions_click', {
                          'event_category': 'navigation',
                          'event_label': 'header_curated_experiences_my_sessions',
                          'click_location': 'header_dropdown'
                        });
                      }
                      navigate('/curated-fika/my-sessions');
                      setIsCuratedExperiencesDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/curated-fika/my-sessions'
                        ? 'bg-amber-50 text-amber-700 border-l-4 border-amber-500'
                        : 'text-gray-700 hover:bg-amber-50 hover:text-amber-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <CalendarDaysIcon className="w-5 h-5 mr-3 text-amber-500" />
                      <div>
                        <div className="font-medium text-gray-800">My Curated Sessions</div>
                        <div className="text-xs text-gray-500">View & manage your bookings</div>
                      </div>
                    </div>
                  </button>

                  <div className="border-t border-gray-200 my-2"></div>

                  {/* Quick Link to Learn More */}
                  <button
                    onClick={() => {
                      navigate('/curated-fika');
                      setIsCuratedExperiencesDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-gray-600 hover:text-purple-600 transition-colors text-sm"
                  >
                    <div className="flex items-center">
                      <span className="mr-2">✨</span>
                      <span>Learn about Curated Experiences</span>
                    </div>
                  </button>
                </div>
              )}
            </div>


            {/* Membership Tab (unchanged) */}
                                
            <div className="relative">
              <button 
                onClick={handleSubscriptionClick}
                className={`text-gray-700 hover:text-orange-600 font-medium transition-colors pb-2 flex items-center gap-2 ${
                  isNavItemActive('subscription') ? 'text-orange-600' : ''
                }`}
              >
                <StarIcon className="w-8 h-8" />
                <div className="flex flex-col items-start">
                  <span>Membership</span>
                    <span className="text-xs text-orange-500 font-medium -mt-1">
                      Plans & Pricing
                    </span>
                </div>
              </button>
              {isNavItemActive('subscription') && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600 rounded-full"></div>
              )}
            </div>
            
            
             
            
            
            
            
             
             
             
            
             
            
            {/* Experiences Tab (unchanged) */}
             {/* 
            <div className="relative">
              <button 
                onClick={handleExperiencesClick}
                className={`text-gray-700 hover:text-purple-600 font-medium transition-colors pb-2 flex items-center gap-2 ${
                  isNavItemActive('experiences') ? 'text-purple-600' : ''
                }`}
              >
                <SparklesIcon className="w-8 h-8" />
                <div className="flex flex-col items-start">
                  <span>Curated Experiences</span>
                  <span className="text-xs text-orange-500 font-medium uppercase tracking-wider -mt-1">
                    Beta
                  </span>
                </div>
              </button>
              {isNavItemActive('experiences') && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black rounded-full"></div>
              )}
            </div>
            */}

            {/* 3-7-26 */}
            {/* Fika Practice Tab */}
             {/*
            <div className="relative">
              <button
                onClick={() => {
                  navigate('/fika-practice/today');
                  setIsMenuOpen(false);
                }}
                className={`text-gray-700 hover:text-amber-600 font-medium transition-colors pb-2 flex items-center gap-2 ${
                  isNavItemActive('fika-practice') ? 'text-amber-600' : ''
                }`}
              >
                <FireIcon className="w-8 h-8" />
                <div className="flex flex-col items-start relative">
                  <span>Practice</span>
                  <span className="text-xs text-amber-500 font-medium -mt-1">Daily Fika</span>
                  
                  {fikaStreakCount > 0 && (
                    <span className="absolute -top-2 -right-6 flex items-center gap-0.5 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none shadow-sm">
                      🔥{fikaStreakCount}
                    </span>
                  )}
                </div>
              </button>
              {isNavItemActive('fika-practice') && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500 rounded-full"></div>
              )}
            </div>
            */}

            {/* NEW: Resources Dropdown */}
            <div className="relative resources-dropdown-container">
              <button 
                onClick={() => setIsResourcesDropdownOpen(!isResourcesDropdownOpen)}
                onMouseEnter={() => setIsResourcesDropdownOpen(true)}
                className={`text-gray-700 hover:text-purple-600 font-medium transition-colors pb-2 flex items-center gap-2 ${
                  isNavItemActive('resources') ? 'text-purple-600' : ''
                }`}
              >
                <AcademicCapIcon className="w-8 h-8" />
                Resources
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${isResourcesDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {isNavItemActive('resources') && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black rounded-full"></div>
              )}
              
              {/* Resources Dropdown Menu */}
              {isResourcesDropdownOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                  onMouseLeave={() => setIsResourcesDropdownOpen(false)}
                >
                  <button
                    onClick={() => {
                      handleMemberPassportClick();
                      setIsResourcesDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/member-passport'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <IdentificationIcon className="w-5 h-5 mr-3 text-purple-500" />
                      <div>
                        <div className="font-medium">Member Passport</div>
                        <div className="text-xs text-gray-500">Track your event journey & upcoming events</div>
                      </div>
                    </div>
                  </button>

                  <div className="border-t border-gray-200 my-2"></div>

                  {/* Event Photos */}
                  <button
                      onClick={handlePhotosClick}
                      className={`w-full text-left px-4 py-3 transition-colors ${
                        location.pathname === '/photos'
                          ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                          : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                      }`}
                    >
                      <div className="flex items-center">
                        <CameraIcon className="w-5 h-5 mr-3 text-indigo-500" />
                        <div>
                          <div className="font-medium">Event Photos</div>
                          <div className="text-xs text-gray-500">Community memories & moments</div>
                        </div>
                      </div>
                  </button>

                  <div className="border-t border-gray-200 my-2"></div>

                  <button
                    onClick={() => {
                      handleKindMomentsClick();
                      setIsResourcesDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/kind-moments'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-amber-50 hover:text-amber-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="mr-3 text-lg">🌍</span>
                      <div>
                        <div className="font-medium text-gray-800">Kind Moments</div>
                        <div className="text-xs text-gray-500">Stories of human kindness worldwide</div>
                      </div>
                    </div>
                  </button>

                   {/* NEW: How to Be Happy */}
                  <button
                    onClick={() => {
                      handleHappinessClick();
                      setIsResourcesDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/happiness'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <HeartIcon className="w-5 h-5 mr-3 text-pink-500" />
                      <div>
                        <div className="font-medium">How to Be Happy</div>
                        <div className="text-xs text-gray-500">Friendship & wellbeing</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      handleConversationStartersClick();
                      setIsResourcesDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/conversation-starters'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <ChatBubbleLeftRightIcon className="w-5 h-5 mr-3 text-green-500" />
                      <div>
                        <div className="font-medium">Conversation Starters & Wellness</div>
                        <div className="text-xs text-gray-500">300+ questions for meaningful connection</div>
                      </div>
                    </div>
                  </button>


                  <div className="border-t border-gray-200 my-2"></div>

                  <button
                    onClick={() => {
                      handleNomadConciergClick();
                      setIsResourcesDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/nomad-concierge'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <GlobeAltIcon className="w-5 h-5 mr-3 text-indigo-500" />
                      <div>
                        <div className="font-medium">Nomad Concierge</div>
                        <div className="text-xs text-gray-500">Live like a local, connect like a member</div>
                      </div>
                    </div>
                  </button>

                   {/* 🆕 NEW: Submit Discovery */}
                  <button
                    onClick={() => {
                      handleSubmitDiscoveryClick();
                      setIsResourcesDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/submit-discovery'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <SparklesIcon className="w-5 h-5 mr-3 text-pink-500" />
                      <div>
                        <div className="font-medium text-gray-800">Share Your Discovery</div>
                        <div className="text-xs text-gray-500">Found a hidden gem? Tell us!</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      handleCoffeeGuideClick();
                      setIsResourcesDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname.includes('/coffee-guide')
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="mr-3 text-lg">☕</span>
                      <div>
                        <div className="font-medium text-gray-800">Cafe Guide</div>
                        <div className="text-xs text-gray-500">Curated specialty coffee shops</div>
                      </div>
                    </div>
                  </button>

                  <div className="border-t border-gray-200 my-2"></div>

                  <button
                    onClick={() => {
                      handleTokyoExpatGuideClick();
                      setIsResourcesDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/tokyo-expat-guide'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <MapPinIcon className="w-5 h-5 mr-3 text-orange-500" />
                      <div>
                        <div className="font-medium text-gray-800">Tokyo Expat Guide</div>
                        <div className="text-xs text-gray-500">Essential guide for new expats</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      handleTokyoExpatCalculatorClick();
                      setIsResourcesDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/tokyo-expat-calculator'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <CalculatorIcon className="w-5 h-5 mr-3 text-blue-500" />
                      <div>
                        <div className="font-medium text-gray-800">Tokyo Expat Calculator</div>
                        <div className="text-xs text-gray-500">Save time, money & loneliness</div>
                      </div>
                    </div>
                  </button>

                  

                  <div className="border-t border-gray-200 my-2"></div>
                      
                  <button
                    onClick={() => {
                      handleGoalAccountabilityPartnersClick();
                      setIsResourcesDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/goal-accountability-partners'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="mr-3 text-lg">🎯</span>
                      <div>
                        <div className="font-medium text-gray-800 flex items-center">
                          Goal Accountability Partners
                          {/*
                          <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                            FREE
                          </span>
                            */}
                        </div>
                        <div className="text-xs text-gray-500">Find your accountability tribe</div>
                      </div>
                    </div>
                  </button>
                  
                  
               

                </div>
              )}
            </div>

            {/* Admin Newsletter Dropdown (unchanged) */}
                 {/*                              
            <div className="relative admin-dropdown-container">
              <button 
                onClick={() => setIsAdminDropdownOpen(!isAdminDropdownOpen)}
                onMouseEnter={() => setIsAdminDropdownOpen(true)}
                className={`text-gray-700 hover:text-purple-600 font-medium transition-colors pb-2 flex items-center gap-2 ${
                  isNavItemActive('admin') ? 'text-purple-600' : ''
                }`}
              >
                <CogIcon className="w-8 h-8" />
                Admin
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${isAdminDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {isNavItemActive('admin') && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black rounded-full"></div>
              )}
              
              {isAdminDropdownOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                  onMouseLeave={() => setIsAdminDropdownOpen(false)}
                >
                  <button
                    onClick={() => {
                      handleNewsletterAdminClick();
                      setIsAdminDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/admin/newsletter'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <EnvelopeIcon className="w-5 h-5 mr-3 text-blue-500" />
                      <div>
                        <div className="font-medium text-gray-800">Newsletter Admin</div>
                        <div className="text-xs text-gray-500">Manage email campaigns</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      handleGoalAccountabilityAdminClick();
                      setIsAdminDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/admin/goal-accountability'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <AdminUsersIcon className="w-5 h-5 mr-3 text-green-500" />
                      <div>
                        <div className="font-medium text-gray-800">Goal Accountability Admin</div>
                        <div className="text-xs text-gray-500">Manage applications & groups</div>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => {
                      handleCuratedFikaAdminClick();
                      setIsAdminDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/admin/curated-fika'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <SparklesIcon className="w-5 h-5 mr-3 text-amber-500" />
                      <div>
                        <div className="font-medium text-gray-800">Curated Fika Admin</div>
                        <div className="text-xs text-gray-500">Manage premium intimate experiences</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      handleCuratedFikaEntrepreneursAdminClick();
                      setIsAdminDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      location.pathname === '/admin/curated-fika-entrepreneurs'
                        ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-500'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <RocketLaunchIcon className="w-5 h-5 mr-3 text-orange-500" />
                      <div>
                        <div className="font-medium text-gray-800">Founders 1-on-1 Admin</div>
                        <div className="text-xs text-gray-500">Manage entrepreneur matching</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      handleDeepFikaAdminClick();
                      setIsAdminDropdownOpen(false);
                    }}
                    className={'w-full text-left px-4 py-3 transition-colors ' + (
                      location.pathname === '/admin/deep-fika'
                        ? 'bg-emerald-50 text-emerald-700 border-l-4 border-emerald-500'
                        : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                    )}
                  >
                    <div className="flex items-center">
                      <HeartIcon className="w-5 h-5 mr-3 text-emerald-500" />
                      <div>
                        <div className="font-medium text-gray-800">Deep Fika Admin</div>
                        <div className="text-xs text-gray-500">1-on-1 matched sessions</div>
                      </div>
                    </div>
                  </button>


                </div>
              )}
            </div> 
            */}
            
            

            
             
            
            
            
            
            
            
            
            
                                                                                                                     
          </nav>

          {/* Desktop CTA Area (unchanged) */}
          {/*  <div className="hidden md:flex items-center space-x-3"> */}
          <div className="hidden md:flex items-center space-x-3 ml-16 pl-4">
            {isMemberLoggedIn && memberData && passportData && PASSPORT_CONFIG.enabled && (
              <div className="flex items-center space-x-3">
                <div className="text-xs text-gray-600 text-right">
                  <div className="font-medium">{memberData.email}</div>
                  <div className="flex items-center text-purple-600">
                    <span className="font-medium">{passportData.stamps} stamps</span>
                    <span className="ml-1">{getTierConfig(passportData.tier)?.icon || '🏷️'}</span>
                  </div>
                </div>
                
                <button
                  onClick={() => navigate('/member-passport')}
                  className="p-2 hover:bg-purple-50 rounded-lg transition-colors group"
                  title="View Member Passport"
                >
                  <IdentificationIcon className="w-5 h-5 text-purple-600 group-hover:text-purple-700" />
                </button>
              </div>
            )}

            {isMemberLoggedIn && memberData && (!passportData || !PASSPORT_CONFIG.enabled) && !loadingPassport && (
              <div className="text-xs text-gray-600 text-right">
                <div className="font-medium">{memberData.email}</div>
                <div className="text-gray-500 text-xs">Dashboard Access</div>
              </div>
            )}

            <button 
              onClick={smartCTA.action}
              className={`${smartCTA.className} text-white px-6 py-2 rounded-lg font-medium hover:scale-105 transition-all flex items-center gap-2`}
            >
              {smartCTA.icon && <smartCTA.icon className="w-4 h-4" />}
              <div className="flex flex-col">
                <span className="text-sm">{smartCTA.text}</span>
                {smartCTA.subtext && (
                  <span className="text-xs opacity-80">{smartCTA.subtext}</span>
                )}
              </div>
            </button>

            {isMemberLoggedIn && (
              <button
                onClick={handleMemberLogout}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Logout"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Mobile Menu Button (unchanged) */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label={isMenuOpen ? "Close mobile menu" : "Open mobile menu"}
          >
            {isMenuOpen ? (
              <XMarkIcon className="w-6 h-6 text-gray-700" />
            ) : (
              <Bars3Icon className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Navigation (updated with reorganization) */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            {/* Add proper height constraints and scrolling */}
            <div 
              className="max-h-[calc(100vh-120px)] overflow-y-auto pb-safe"
              style={{ 
                maxHeight: 'calc(100vh - 120px)', // More conservative height calculation
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
                paddingBottom: 'env(safe-area-inset-bottom, 20px)' // Safe area for mobile
              }}
            >
              <nav className="flex flex-col space-y-4 py-4">
                {/* Mobile Member Status */}
                {isMemberLoggedIn && memberData && passportData && PASSPORT_CONFIG.enabled && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 mx-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-green-800">{memberData.email}</div>
                        <div className="text-xs text-green-600 flex items-center">
                          <span>{passportData.stamps} stamps</span>
                          <span className="ml-1">{getTierConfig(passportData.tier)?.icon}</span>
                          <span className="mx-2">•</span>
                          <span className="capitalize">{passportData.tier}</span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate('/member-passport')}
                          className="bg-green-500 text-white p-2 rounded hover:bg-green-600 transition-colors"
                          title="View Passport"
                        >
                          <IdentificationIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleMemberDashboardClick}
                          className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                        >
                          Dashboard
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Mobile Member Login */}
                {!isMemberLoggedIn && (location.pathname.includes('goal-accountability') || location.pathname.includes('member')) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 mx-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-blue-800">Member Access</div>
                        <div className="text-xs text-blue-600">Already have an account?</div>
                      </div>
                      <button
                        onClick={handleMemberLoginClick}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors flex items-center gap-1"
                      >
                        <ArrowRightOnRectangleIcon className="w-3 h-3" />
                        Goal Login
                      </button>
                    </div>
                  </div>
                )}

                {/* Add padding to all navigation items */}
                {/*  <CalendarDaysIcon className="w-7 h-7" /> */}
                <div className="px-4 space-y-4">

                   {/* Home Navigation - Mobile */}
                    <div className="relative">
                      <button 
                        onClick={handleLogoClick}
                        className={`text-left text-gray-700 hover:text-purple-600 font-medium py-2 transition-colors w-full flex items-center gap-3 ${
                          location.pathname === '/' ? 'text-purple-600' : ''
                        }`}
                      >
                        <svg width="28" height="30" viewBox="0 0 30 32" className="flex-shrink-0">
                          <defs>
                            <linearGradient id="mobileInnerCGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" style={{ stopColor: '#FBBF24' }} />
                              <stop offset="100%" style={{ stopColor: '#F97316' }} />
                            </linearGradient>
                          </defs>
                          <path
                            d="M 24 10 A 10 10 0 1 0 24 22"
                            fill="none"
                            stroke="#9333EA"
                            strokeWidth="4"
                            strokeLinecap="round"
                          />
                          <path
                            d="M 21 12 A 7 7 0 1 0 21 20"
                            fill="none"
                            stroke="url(#mobileInnerCGradient)"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="flex flex-col items-start">
                          <span>Home</span>
                          <span className="text-xs text-amber-600 -mt-0.5">
                            Back to main page
                          </span>
                        </div>
                      </button>
                      {location.pathname === '/' && (
                        <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-purple-600 rounded-full"></div>
                      )}
                    </div>

                    <div className="border-t border-gray-200 my-2"></div>

                   {/* 🆕 MEMBERSHIP tab - Mobile Section */}
                          
                      <div className="relative">
                        <button 
                          onClick={handleSubscriptionClick}
                          className={`text-left text-gray-700 hover:text-orange-600 font-medium py-2 transition-colors w-full flex items-center gap-3 ${
                            isNavItemActive('subscription') ? 'text-orange-600' : ''
                          }`}
                        >
                          <StarIcon className="w-7 h-7" />
                          <div className="flex flex-col items-start">
                            <span>Membership</span>
                            <span className="text-xs text-orange-500 font-medium -mt-0.05">
                              Plans & Pricing
                            </span>
                          </div>
                        </button>
                        {isNavItemActive('subscription') && (
                          <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-orange-600 rounded-full"></div>
                        )}
                      </div>

                    <div className="border-t border-gray-200 my-2"></div>
                    

                  {/* Events & Experiences */}
                  <div className="relative">
                    <button 
                      onClick={handleEventsClick}
                      className={`text-left text-gray-700 hover:text-purple-600 font-medium py-2 transition-colors w-full flex items-center gap-3 ${
                        isNavItemActive('events') ? 'text-purple-600' : ''
                      }`}
                    >
                      <SparklesIcon className="w-5 h-5 mr-3 text-amber-500" />
                      <div className="flex flex-col items-start">
                        <span>Experiences</span>
                        <span className="text-xs text-gray-500 -mt-0.5">
                          View upcoming events
                        </span>
                      </div>
                    </button>
                    {isNavItemActive('events') && (
                      <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-black rounded-full"></div>
                    )}
                  </div>

                  <div className="border-t border-gray-200 my-2"></div>

                    {/* 3-7-26 */}
                  {/* Fika Practice — Mobile */}
                  <div className="relative">
                    <button
                      onClick={() => {
                        navigate('/fika-practice/today');
                        setIsMenuOpen(false);
                      }}
                      className={`text-left font-medium py-2 transition-colors w-full flex items-center gap-3 ${
                        isNavItemActive('fika-practice')
                          ? 'text-amber-600'
                          : 'text-gray-700 hover:text-amber-600'
                      }`}
                    >
                      <FireIcon className="w-7 h-7 text-amber-500" />
                      <div className="flex flex-col items-start">
                        <span className="flex items-center gap-2">
                          Practice
                          {fikaStreakCount > 0 && (
                            <span className="flex items-center gap-0.5 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                              🔥{fikaStreakCount}
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-amber-500 -mt-0.5">Daily Fika · 2 min</span>
                      </div>
                    </button>
                    {isNavItemActive('fika-practice') && (
                      <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-amber-500 rounded-full"></div>
                    )}
                  </div>

                   <div className="border-t border-gray-200 my-2"></div>

                  <button
                    onClick={() => {
                      if (typeof gtag !== 'undefined') {
                        gtag('event', 'why_connectco_mobile_click', {
                          'event_category': 'navigation',
                          'event_label': 'mobile_why_connectco',
                          'click_location': 'mobile_menu_fika'
                        });
                      }
                      navigate('/why-connectco');
                      setIsMenuOpen(false);
                    }}
                    className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                      location.pathname === '/why-connectco'
                        ? 'text-emerald-600'
                        : 'text-gray-700 hover:text-emerald-600'
                    }`}
                  >
                    <span className="mr-3">🌿</span>
                    <div>
                      <div className="font-medium">Why ConnectCo?</div>
                      <div className="text-xs text-gray-500">Our approach vs. the alternatives</div>
                    </div>
                  </button>

                       {/*
                   <div className="border-t border-gray-200 my-2"></div>
                   */}

                  {/* Community Section — Mobile */}
                  <div className="border-t border-gray-200 mt-4 pt-2">
                    <div className="text-gray-500 text-sm font-medium mb-2 uppercase tracking-wider flex items-center gap-2 px-1">
                      <ChatBubbleLeftRightIcon className="w-4 h-4" />
                      Community
                    </div>

                    <div className="space-y-1">
                      {/* Fika Board */}
                      <div className="relative">
                        <button
                          onClick={() => { handleCommunityClick(); }}
                          className={`text-left font-medium py-2 pl-4 transition-colors w-full flex items-center gap-3 ${
                            isNavItemActive('community') ? 'text-purple-600' : 'text-gray-700 hover:text-purple-600'
                          }`}
                        >
                          <ChatBubbleLeftRightIcon className="w-5 h-5 text-purple-500" />
                          <div>
                            <div className="font-medium">Fika Board</div>
                            <div className="text-xs text-gray-500">Community discussions</div>
                          </div>
                        </button>
                        {isNavItemActive('community') && (
                          <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-purple-500 rounded-full"></div>
                        )}
                      </div>

                      {/* Stories */}
                      <div className="relative">
                        <button
                          onClick={handleBlogClick}
                          className={`text-left font-medium py-2 pl-4 transition-colors w-full flex items-center gap-3 ${
                            isNavItemActive('blog') ? 'text-purple-600' : 'text-gray-700 hover:text-purple-600'
                          }`}
                        >
                          <BookOpenIcon className="w-5 h-5 text-indigo-500" />
                          <div>
                            <div className="font-medium">Stories</div>
                            <div className="text-xs text-gray-500">Articles on friendship & fika</div>
                          </div>
                        </button>
                        {isNavItemActive('blog') && (
                          <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-black rounded-full"></div>
                        )}
                      </div>

                      {/* Photos */}
                      <div className="relative">
                        <button
                          onClick={() => { handlePhotosClick(); }}
                          className={`text-left font-medium py-2 pl-4 transition-colors w-full flex items-center gap-3 ${
                            location.pathname === '/photos' ? 'text-purple-600' : 'text-gray-700 hover:text-purple-600'
                          }`}
                        >
                          <CameraIcon className="w-5 h-5 text-pink-500" />
                          <div>
                            <div className="font-medium">Photos</div>
                            <div className="text-xs text-gray-500">Moments from our gatherings</div>
                          </div>
                        </button>
                        {location.pathname === '/photos' && (
                          <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-black rounded-full"></div>
                        )}
                      </div>
                    </div>
                  </div>


                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-t border-gray-200 mt-4">
                  </div>
                  
                  {/* Mobile Fika Section */}
                  <div className="py-2">
                    <div className="text-gray-500 text-sm font-medium mb-3 uppercase tracking-wider flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center">☕</div>
                      Fika
                    </div>
                    
                    <div className="space-y-2">
                      <button
                        onClick={handleMonthlyFikaClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/monthly-fika'
                            ? 'text-amber-600' 
                            : 'text-gray-700 hover:text-amber-600'
                        }`}
                      >
                        <span className="mr-3">📅</span>
                        <div>
                          <div className="font-medium">Monthly Fika</div>
                          <div className="text-xs text-gray-500">Open to All Subscribers</div>
                        </div>
                      </button>
                      
                      <button
                        onClick={handleSignatureFikaClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/signature-fika' 
                            ? 'text-amber-600' 
                            : 'text-gray-700 hover:text-amber-600'
                        }`}
                      >
                        <span className="mr-3">⭐</span>
                        <div>
                          <div className="font-medium">Signature Fika</div>
                          <div className="text-xs text-gray-500">Weekly Premium</div>
                        </div>
                      </button>

                      


                      <button
                        onClick={handleWhatIsCuratedFikaClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/curated-fika' 
                            ? 'text-purple-600' 
                            : 'text-gray-700 hover:text-purple-600'
                        }`}
                      >
                        <span className="mr-3 text-lg">✨</span>
                        <div>
                          <div className="font-medium">What is Curated Fika?</div>
                          <div className="text-xs text-gray-500">Learn More</div>
                        </div>
                      </button>

                            

                      <div className="border-t border-gray-200 my-2"></div>

                      {/* 12-25-25 */}
                      {/* NEW: Curated Experiences Section - Mobile */}
                      <div className="py-2">
                        <div className="text-gray-500 text-sm font-medium mb-3 uppercase tracking-wider flex items-center gap-2">
                          <SparklesIcon className="w-6 h-6" />
                          Curated Fika
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium ml-auto">
                            Premium
                          </span>
                        </div>

                        <button
                        onClick={handleCuratedFikaClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/curated-fika-signup' 
                            ? 'text-amber-600' 
                            : 'text-gray-700 hover:text-amber-600'
                        }`}
                      >
                        <SparklesIcon className="w-5 h-5 mr-3 text-amber-500" />
                        <div>
                          <div className="font-medium flex items-center">
                            Join Curated Fika
                            {/*
                            <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                              ✨ Coming Soon
                            </span>
                          
                            <span className="ml-2 px-1.5 py-0.5 bg-gradient-to-r from-purple-600 to-amber-600 text-purple-100 text-xs rounded-full font-medium">
                              BETA
                            </span>
                             */}
                          
                          </div>
                          <div className="text-xs text-gray-500">Ultra-Premium • 3-4 People</div>
                        </div>
                      </button>
                        
                        <div className="space-y-2">
                          <button
                            onClick={() => {
                              if (typeof gtag !== 'undefined') {
                                gtag('event', 'curated_experiences_member_portal_mobile_click', {
                                  'event_category': 'navigation',
                                  'event_label': 'mobile_curated_experiences_member_portal',
                                  'click_location': 'mobile_menu'
                                });
                              }
                              navigate('/curated-fika/member-portal');
                              setIsMenuOpen(false);
                            }}
                            className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                              location.pathname === '/curated-fika/member-portal'
                                ? 'text-purple-600'
                                : 'text-gray-700 hover:text-purple-600'
                            }`}
                          >
                            <UserCircleIcon className="w-5 h-5 mr-3 text-purple-500" />
                            <div>
                              <div className="font-medium">Browse Sessions</div>
                              <div className="text-xs text-gray-500">View personalized matches</div>
                            </div>
                          </button>

                          <button
                            onClick={() => {
                              if (typeof gtag !== 'undefined') {
                                gtag('event', 'curated_experiences_my_sessions_mobile_click', {
                                  'event_category': 'navigation',
                                  'event_label': 'mobile_curated_experiences_my_sessions',
                                  'click_location': 'mobile_menu'
                                });
                              }
                              navigate('/curated-fika/my-sessions');
                              setIsMenuOpen(false);
                            }}
                            className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                              location.pathname === '/curated-fika/my-sessions'
                                ? 'text-amber-600'
                                : 'text-gray-700 hover:text-amber-600'
                            }`}
                          >
                            <CalendarDaysIcon className="w-5 h-5 mr-3 text-amber-500" />
                            <div>
                              <div className="font-medium">My Curated Sessions</div>
                              <div className="text-xs text-gray-500">Manage Your Bookings</div>
                            </div>
                          </button>

                          <button
                            onClick={() => {
                              navigate('/curated-fika');
                              setIsMenuOpen(false);
                            }}
                            className="w-full text-left py-2 pl-4 transition-colors flex items-center text-gray-600 hover:text-purple-600"
                          >
                            <span className="mr-3 text-lg">✨</span>
                            <div>
                              <div className="font-medium text-sm">Learn More</div>
                            </div>
                          </button>
                        </div>
                      </div>

                      <div className="border-t border-gray-200 my-2"></div>

                      {/* 🆕 MEMBERSHIP tab - Mobile Section */}
                         {/* 
                      <div className="relative">
                        <button 
                          onClick={handleSubscriptionClick}
                          className={`text-left text-gray-700 hover:text-orange-600 font-medium py-2 transition-colors w-full flex items-center gap-3 ${
                            isNavItemActive('subscription') ? 'text-orange-600' : ''
                          }`}
                        >
                          <StarIcon className="w-7 h-7" />
                          <div className="flex flex-col items-start">
                            <span>Membership</span>
                            <span className="text-xs text-orange-500 font-medium -mt-0.05">
                              Plans & Pricing
                            </span>
                          </div>
                        </button>
                        {isNavItemActive('subscription') && (
                          <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-orange-600 rounded-full"></div>
                        )}
                      </div>
                       */}
                      

                      
                      {/* Mobile: Deep Fika (1-on-1) */}
                      <button
                        onClick={handleDeepFikaClick}
                        className={'w-full text-left py-2 pl-4 transition-colors flex items-center ' + (
                          location.pathname === '/deep-fika' || location.pathname === '/deep-fika/apply'
                            ? 'text-emerald-600'
                            : 'text-gray-700 hover:text-emerald-600'
                        )}
                      >
                        <span className="mr-3 text-lg">🫂</span>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            Deep Fika
                            <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                              1-on-1
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">One matched conversation</div>
                        </div>
                      </button>

                      {/* Mobile: Founders Section Divider */}
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-t border-gray-200 mt-4">
                        For Founders & Entreprenuers
                      </div>

                      {/* Mobile: Curated Fika Founders (1-on-1) */}
                      <button
                        onClick={handleCuratedFikaFoundersClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/curated-fika/founders'
                            ? 'text-indigo-600'
                            : 'text-gray-700 hover:text-indigo-600'
                        }`}
                      >
                        <span className="mr-3 text-lg">💼</span>
                        <div>
                          <div className="font-medium">What is Builder Coffee Match
                           <span className="ml-2 px-1.5 py-0.5 bg-gradient-to-r from-purple-600 to-amber-600 text-purple-100 text-xs rounded-full font-medium">
                              BETA
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">Confidential Conversations</div>
                        </div>
                      </button>

                      {/* Mobile: Apply for Builder Coffee Match (Action Button) */}
                        {/*
                      <button
                        onClick={() => {
                          handleBuilderCoffeeMatchApplyClick();
                          setIsMenuOpen(false);
                        }}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/curated-fika/founders-1on1/apply'
                            ? 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border-l-4 border-indigo-600'
                            : 'text-gray-700 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-700'
                        }`}
                      >
                        <span className="mr-3 text-lg">📝</span>
                        <div className="flex-1">
                          <div className="font-semibold flex items-center">
                            Apply for Builder Coffee Match
                            <span className="ml-2 px-1.5 py-0.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs rounded-full font-medium">
                              BETA
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">Start your application</div>
                        </div>
                      </button>
                      */}

                      {/* Mobile: Founders Fika (Group) */}
                       {/*
                      <button
                        onClick={handleFoundersFikaClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/founders-fika'
                            ? 'text-indigo-600'
                            : 'text-gray-700 hover:text-indigo-600'
                        }`}
                      >
                        <span className="mr-3 text-lg">🤝</span>
                        <div>
                          <div className="font-medium">Peer Support Group</div>
                          <div className="text-xs text-gray-500">6-8 Founders Monthly</div>
                        </div>
                      </button>
                      */}

                       {/* Mobile: Founders Section Divider */}
                       <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-t border-gray-200 mt-4">
                        Learn More
                      </div>

                      <button
                        onClick={handleWhatIsFikaClick}          
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/what-is-fika' 
                            ? 'text-amber-600' 
                            : 'text-gray-700 hover:text-amber-600'
                        }`}
                      >
                        <span className="mr-3">🇸🇪</span>
                        <div>
                          <div className="font-medium">What is Fika?</div>
                        </div>
                      </button>

                      <button
                        onClick={() => {
                          handleFikaAt33Click();
                          setIsMenuOpen(false);
                        }}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/fika-at-33'
                            ? 'text-amber-500'
                            : 'text-gray-700 hover:text-amber-500'
                        }`}
                      >
                        <span className="mr-3">🎵</span>
                        <div>
                          <div className="font-medium">What is Fika at 33⅓?</div>
                          <div className="text-xs text-gray-500">Vinyl Listening</div>
                        </div>
                      </button>

                      <button
                        onClick={handleWhatIsSpeedFikaClick}          
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/what-is-speed-fika' 
                            ? 'text-amber-600' 
                            : 'text-gray-700 hover:text-amber-600'
                        }`}
                      >
                        <span className="mr-3">⚡</span>
                        <div>
                          <div className="font-medium">What is Speed Fika?</div>
                          <div className="text-xs text-gray-500">Anti-Speed Dating</div>
                        </div>
                      </button>

                      <button
                        onClick={handleGoalAccountabilityPartnersClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/goal-accountability-partners' 
                            ? 'text-purple-600' 
                            : 'text-gray-700 hover:text-purple-600'
                        }`}
                      >
                        <span className="mr-3 text-lg">🎯</span>
                        <div>
                          <div className="font-medium flex items-center">
                            Goal Accountability
                            
                          </div>
                          <div className="text-xs text-gray-500">Find Your Tribe</div>
                        </div>
                      </button>
                    </div>
                  </div>

                 

                  {/* Mobile Resources Section */}
                  <div className="py-2">
                    <div className="text-gray-500 text-sm font-medium mb-3 uppercase tracking-wider flex items-center gap-2">
                      <AcademicCapIcon className="w-6 h-6" />
                      Resources
                    </div>
                    
                    <div className="space-y-2">
                      <button
                        onClick={handleMemberPassportClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/member-passport' 
                            ? 'text-purple-600' 
                            : 'text-gray-700 hover:text-purple-600'
                        }`}
                      >
                        <IdentificationIcon className="w-5 h-5 mr-3 text-purple-500" />
                        <div>
                          <div className="font-medium">Member Passport</div>
                          <div className="text-xs text-gray-500">Track Your Journey & Upcoming Events</div>
                        </div>
                      </button>

                        {/* Event Photos - Mobile version */}
                      <button
                        onClick={handlePhotosClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/photos' 
                            ? 'text-purple-600' 
                            : 'text-gray-700 hover:text-purple-600'
                        }`}
                      >
                        <CameraIcon className="w-5 h-5 mr-3 text-indigo-500" />
                        <div>
                          <div className="font-medium">Event Photos</div>
                          <div className="text-xs text-gray-500">Community Memories</div>
                        </div>
                      </button>

                         {/* NEW: Kind Moments - Mobile version */}
                      <button
                        onClick={handleKindMomentsClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/kind-moments'
                            ? 'text-purple-600'
                            : 'text-gray-700 hover:text-amber-600'
                        }`}
                      >
                        <span className="mr-3 text-lg">🌍</span>
                        <div>
                          <div className="font-medium">Kind Moments</div>
                          <div className="text-xs text-gray-500">Stories of Human Kindness</div>
                        </div>
                      </button>

                        {/* NEW: How to Be Happy - Mobile version */}
                        <button
                          onClick={handleHappinessClick}
                          className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                            location.pathname === '/happiness' 
                              ? 'text-purple-600' 
                              : 'text-gray-700 hover:text-purple-600'
                          }`}
                        >
                          <HeartIcon className="w-5 h-5 mr-3 text-pink-500" />
                          <div>
                            <div className="font-medium">How to Be Happy</div>
                            <div className="text-xs text-gray-500">Friendship & Wellbeing</div>
                          </div>
                        </button>

                      <button
                        onClick={handleConversationStartersClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/conversation-starters' 
                            ? 'text-purple-600' 
                            : 'text-gray-700 hover:text-purple-600'
                        }`}
                      >
                        <ChatBubbleLeftRightIcon className="w-5 h-5 mr-3 text-green-500" />
                        <div>
                          <div className="font-medium">Conversation Starters & Wellness</div>
                          <div className="text-xs text-gray-500">300+ Questions</div>
                        </div>
                      </button>

                      <button
                        onClick={handleTokyoExpatGuideClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/tokyo-expat-guide' 
                            ? 'text-purple-600' 
                            : 'text-gray-700 hover:text-purple-600'
                        }`}
                      >
                        <MapPinIcon className="w-5 h-5 mr-3 text-orange-500" />
                        <div>
                          <div className="font-medium">Tokyo Expat Guide</div>
                          <div className="text-xs text-gray-500">Essential Guide</div>
                        </div>
                      </button>

                      <button
                        onClick={handleTokyoExpatCalculatorClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/tokyo-expat-calculator' 
                            ? 'text-purple-600' 
                            : 'text-gray-700 hover:text-purple-600'
                        }`}
                      >
                        <CalculatorIcon className="w-5 h-5 mr-3 text-blue-500" />
                        <div>
                          <div className="font-medium">Tokyo Expat Calculator</div>
                          <div className="text-xs text-gray-500">Save Time & Money</div>
                        </div>
                      </button>

                      <button
                        onClick={handleNomadConciergClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/nomad-concierge' 
                            ? 'text-purple-600' 
                            : 'text-gray-700 hover:text-purple-600'
                        }`}
                      >
                        <GlobeAltIcon className="w-5 h-5 mr-3 text-indigo-500" />
                        <div>
                          <div className="font-medium">Nomad Concierge</div>
                          <div className="text-xs text-gray-500">Live Like a Local</div>
                        </div>
                      </button>

                       {/* 🆕 Mobile Submit Discovery */}
                      <button
                        onClick={handleSubmitDiscoveryClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname === '/submit-discovery' 
                            ? 'text-purple-600' 
                            : 'text-gray-700 hover:text-purple-600'
                        }`}
                      >
                        <SparklesIcon className="w-5 h-5 mr-3 text-pink-500" />
                        <div>
                          <div className="font-medium">Share Your Discovery</div>
                          <div className="text-xs text-gray-500">Found a Hidden Gem?</div>
                        </div>
                      </button>

                        {/* 🆕 Mobile Cafe Guide*/}
                      <button
                        onClick={handleCoffeeGuideClick}
                        className={`w-full text-left py-2 pl-4 transition-colors flex items-center ${
                          location.pathname.includes('/coffee-guide')
                            ? 'text-purple-600' 
                            : 'text-gray-700 hover:text-purple-600'
                        }`}
                      >
                        <span className="mr-3 text-lg">☕</span>
                        <div>
                          <div className="font-medium">Cafe Guide</div>
                          <div className="text-xs text-gray-500">200+ Specialty Coffee Shops</div>
                        </div>
                      </button>

                    </div>
                  </div>
                  
                  {/* Experiences Tab - Now properly positioned */}
                   {/*
                  <div className="relative">
                    <button 
                      onClick={handleExperiencesClick}
                      className={`text-left text-gray-700 hover:text-purple-600 font-medium py-2 transition-colors w-full flex items-center gap-3 ${
                        isNavItemActive('experiences') ? 'text-purple-600' : ''
                      }`}
                    >
                      <SparklesIcon className="w-7 h-7" />
                      <div className="flex flex-col items-start">
                        <span>Experiences</span>
                        <span className="text-xs text-orange-500 font-medium uppercase tracking-wider">
                          Beta
                        </span>
                      </div>
                    </button>
                    {isNavItemActive('experiences') && (
                      <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-black rounded-full"></div>
                    )}
                  </div>
                  */}

                  {/* Mobile CTA Button */}
                  <div className="space-y-3 mt-6 pt-4 border-t border-gray-200">
                    <button 
                      onClick={smartCTA.action}
                      className={`${smartCTA.className} text-white px-6 py-3 rounded-lg font-medium transition-all text-center w-full flex items-center justify-center gap-2`}
                    >
                      {smartCTA.icon && <smartCTA.icon className="w-4 h-4" />}
                      <div className="flex flex-col">
                        <span className="text-sm">{smartCTA.text}</span>
                        {smartCTA.subtext && (
                          <span className="text-xs opacity-80">{smartCTA.subtext}</span>
                        )}
                      </div>
                    </button>

                    {/* Mobile Logout Button */}
                    {isMemberLoggedIn && (
                      <button
                        onClick={handleMemberLogout}
                        className="w-full bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-medium hover:bg-gray-300 transition-all flex items-center justify-center gap-2"
                      >
                        <ArrowRightOnRectangleIcon className="w-4 h-4" />
                        Logout
                      </button>
                    )}
                  </div>

                  {/* Generous bottom padding for safe scrolling and button visibility */}
                  <div className="h-16 pb-8"></div>
                </div>
              </nav>
            </div>
          </div>
        )}
        
      </div>
    </header>
  );
}

export default Header;